import { supabase } from './supabase';

export type UpsellType = 'ondertapijt' | 'vlekkenbaas' | 'rand-reparatie' | 'ondertapijt-standalone' | 'klant-eigen-item';

export type UpsellBron = 'Office-App' | 'Phone-Agent' | 'Brein' | 'Kantoor-Handmatig';

export interface UpsellEligibilityItem {
  id: string;
  name: string;
  m2: number | null;
  priceInclVat: number;
  priceWithDiscountInclVat: number;
  tweedelig?: boolean;
  unavailable?: boolean;
  unavailableReason?: string;
  /** Kleed heeft al een ondertapijt (verrijkt uit de mirror, niet uit Apex). */
  alreadyHasUnderlay?: boolean;
  /** Kleed is nog niet nagemeten — bestellen mag, snijden pas na voorinspectie. */
  wachtOpNameten?: boolean;
}

export interface UpsellEligibility {
  success: boolean;
  message?: string;
  errorCode?: string;
  customerName?: string;
  workOrderNumber?: string;
  upsellType: UpsellType;
  alreadyOrdered: boolean;
  pricePerM2InclVat: number;
  discountPercentage: number;
  totalPriceInclVat: number;
  totalPriceWithDiscountInclVat: number;
  savingsInclVat: number;
  items: UpsellEligibilityItem[];
}

export interface UpsellResult {
  success: boolean;
  message?: string;
  errorCode?: string;
  alreadyOrdered?: boolean;
  childItemId?: string;
  /** WI-nummer (Name) van het nieuwe item — bv. bij bestaand ondertapijt om de rol te labelen. */
  childItemName?: string;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
  return h;
}

export async function getUpsellEligibility(
  workOrderId: string,
  upsellType: UpsellType,
  discount: number = 0
): Promise<UpsellEligibility> {
  const headers = await authHeaders();
  const qp = new URLSearchParams({ workOrderId, upsellType, discount: String(discount) });
  const response = await fetch(`/api/orders/upsell?${qp.toString()}`, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || err.detail || 'Upsell eligibility ophalen mislukt');
  }
  const data = await response.json();
  return {
    success: data.success === true,
    message: data.message,
    errorCode: data.errorCode,
    customerName: data.customerName,
    workOrderNumber: data.workOrderNumber,
    upsellType: (data.upsellType || upsellType) as UpsellType,
    alreadyOrdered: data.alreadyOrdered === true,
    pricePerM2InclVat: Number(data.pricePerM2InclVat || 0),
    discountPercentage: Number(data.discountPercentage || 0),
    totalPriceInclVat: Number(data.totalPriceInclVat || 0),
    totalPriceWithDiscountInclVat: Number(data.totalPriceWithDiscountInclVat || 0),
    savingsInclVat: Number(data.savingsInclVat || 0),
    items: Array.isArray(data.items) ? data.items : [],
  };
}

export interface AddUpsellArgs {
  workOrderId: string;
  type: UpsellType;
  itemIds?: string[];
  quantity?: number;
  discount?: number;
  bron?: UpsellBron;
}

export async function addUpsell(args: AddUpsellArgs): Promise<UpsellResult> {
  const headers = await authHeaders();
  const body = {
    workOrderId: args.workOrderId,
    upsellType: args.type,
    itemIds: args.itemIds,
    quantity: args.quantity,
    discount: args.discount,
    bron: args.bron || 'Office-App',
  };
  const response = await fetch('/api/orders/upsell', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({} as UpsellResult));
  if (!response.ok && data.success !== true) {
    return {
      success: false,
      message: data.message || `Upsell toevoegen mislukt (${response.status})`,
      errorCode: data.errorCode,
      alreadyOrdered: data.alreadyOrdered,
    };
  }
  return data as UpsellResult;
}

export interface AddStandaloneOndertapijtArgs {
  workOrderId: string;
  lengteCm: number;
  breedteCm: number;
  discount?: number;
  bron?: UpsellBron;
}

export async function addStandaloneOndertapijt(args: AddStandaloneOndertapijtArgs): Promise<UpsellResult> {
  const headers = await authHeaders();
  const body = {
    workOrderId: args.workOrderId,
    upsellType: 'ondertapijt-standalone' as UpsellType,
    lengteCm: args.lengteCm,
    breedteCm: args.breedteCm,
    discount: args.discount,
    bron: args.bron || 'Office-App',
  };
  const response = await fetch('/api/orders/upsell', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({} as UpsellResult));
  if (!response.ok && data.success !== true) {
    return {
      success: false,
      message: data.message || data.error || `Standalone ondertapijt toevoegen mislukt (${response.status})`,
      errorCode: data.errorCode,
    };
  }
  return data as UpsellResult;
}

export interface NabestelOndertapijtResult {
  success: boolean;
  message?: string;
  werkOrderId?: string;
  werkOrderName?: string;
  status?: string;
  paymentUrl?: string;
  shippingCents?: number;
  chargedCents?: number;
  warning?: string;
}

export interface NabestelOndertapijtArgs {
  workOrderId: string;
  itemIds: string[];
  /** % korting op de ondertapijt-prijs; default 30 (aanbieding). */
  discount?: number;
  /** Verzendkosten-override in centen; anders auto (€6,50 < €75, gratis erboven). */
  shippingCents?: number;
}

/**
 * Nabestelling ondertapijt MET verzendkosten, na aflevering. Maakt een nieuwe
 * webshop-order (PostNL) tegen de aanbiedingsprijs + verzendkosten en geeft de
 * Mollie-betaallink terug. Voor orders die al terminal zijn (Teruggebracht/
 * Afgerond) — waar de gewone upsell-flow niet meer op werkt.
 */
export async function nabestelOndertapijtVerzenden(
  args: NabestelOndertapijtArgs
): Promise<NabestelOndertapijtResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/nabestel-ondertapijt', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      workOrderId: args.workOrderId,
      itemIds: args.itemIds,
      discount: args.discount,
      shippingCents: args.shippingCents,
    }),
  });
  const data = await response.json().catch(() => ({} as Record<string, unknown>));
  if (!response.ok || data.success !== true) {
    return {
      success: false,
      message: (data.error as string) || (data.message as string) || `Nabestelling mislukt (${response.status})`,
    };
  }
  return { success: true, ...(data as Omit<NabestelOndertapijtResult, 'success'>) };
}

export interface AddKlantEigenItemArgs {
  workOrderId: string;
  /** Type/label van het losse item: 'Ondertapijt', 'Spanbanden', of vrije tekst. */
  omschrijving: string;
  lengteCm?: number;
  breedteCm?: number;
  bron?: UpsellBron;
}

/**
 * Tagt een LOS, KLANT-EIGEN item à €0 op order-niveau (ondertapijt, spanbanden, …):
 * niet op de factuur, gaat netjes mee terug. Geeft het nieuwe WI-nummer terug
 * (childItemName) zodat de medewerker de fysieke rol/het item kan labelen.
 */
export async function addKlantEigenItem(args: AddKlantEigenItemArgs): Promise<UpsellResult> {
  const headers = await authHeaders();
  const body = {
    workOrderId: args.workOrderId,
    upsellType: 'klant-eigen-item' as UpsellType,
    omschrijving: args.omschrijving,
    lengteCm: args.lengteCm,
    breedteCm: args.breedteCm,
    bron: args.bron || 'Office-App',
  };
  const response = await fetch('/api/orders/upsell', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({} as UpsellResult));
  if (!response.ok && data.success !== true) {
    return {
      success: false,
      message: data.message || data.error || `Los item taggen mislukt (${response.status})`,
      errorCode: data.errorCode,
    };
  }
  return data as UpsellResult;
}
